import type { UseBlotterTableProps } from '@talos/kyoko';
import { useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { getErrorMessage as getWsErrorMessage } from '../providers/AppStateProvider/wsEndpointBuilder';

export interface BlotterQueryResultLike<TData, TError = unknown> {
  data?: TData;
  currentData?: TData;
  error?: TError | null;
  isError?: boolean;
}

type BlotterQueryDataLike<TRow> = ReadonlyMap<unknown, TRow> | Iterable<TRow>;
type RowFromQueryData<TQueryData> =
  TQueryData extends ReadonlyMap<unknown, infer TRow> ? TRow : TQueryData extends Iterable<infer TRow> ? TRow : never;

interface UseQueryBlotterDataObservableParams<TQueryData extends BlotterQueryDataLike<unknown>, TQueryError = unknown> {
  queryResult: BlotterQueryResultLike<TQueryData, TQueryError>;
}

function getRowsFromQueryData<TQueryData extends BlotterQueryDataLike<unknown>>(
  queryData: TQueryData
): RowFromQueryData<TQueryData>[] {
  if (queryData instanceof Map) {
    return Array.from(queryData.values()) as RowFromQueryData<TQueryData>[];
  }
  return Array.from(queryData as Iterable<RowFromQueryData<TQueryData>>);
}

/**
 * Converts a query-hook result into blotter observables. Since queries always work with absolute pieces of data (all or nothing),
 * the dataObservable returned only ever passes initial: true messages.
 *
 * This hook is equivalent to useSubscription in its stability of the output dataObservable. For some, this is undesirable.
 * In time there will be better built alternatives to this approach.
 *
 * Problems with this approach:
 * 1. No delta messages, only absolutes, which are heavy
 * 2. Stable observables across unique requests (potentially), which can lead to stale data issues in error and edge cases
 */
export function useQueryBlotterDataObservable<TQueryData extends BlotterQueryDataLike<unknown>, TQueryError = unknown>({
  queryResult,
}: UseQueryBlotterDataObservableParams<TQueryData, TQueryError>): {
  dataObservable: UseBlotterTableProps<RowFromQueryData<TQueryData>>['dataObservable'];
  errorObservable?: UseBlotterTableProps<RowFromQueryData<TQueryData>>['errorObservable'];
} {
  const [dataSubject] = useState(() => new ReplaySubject<{ initial: true; data: RowFromQueryData<TQueryData>[] }>(1));
  const dataObservable = useMemo(() => dataSubject.asObservable(), [dataSubject]);
  const [errorSubject] = useState(() => new BehaviorSubject<{ error?: { msg: string } } | undefined>(undefined));
  const errorObservable = useMemo(() => errorSubject.asObservable(), [errorSubject]);

  useEffect(() => {
    if (queryResult.data == null) {
      return;
    }

    dataSubject.next({
      initial: true,
      data: getRowsFromQueryData(queryResult.data),
    });
    errorSubject.next(undefined);
  }, [dataSubject, errorSubject, queryResult.data]);

  const hasNoData = queryResult.data == null && queryResult.currentData == null;
  const shouldShowError = queryResult.error != null && (queryResult.isError ?? true) && hasNoData;
  const errorMessage = useMemo(() => {
    if (!shouldShowError) {
      return undefined;
    }
    return getWsErrorMessage(queryResult.error);
  }, [queryResult.error, shouldShowError]);

  useEffect(() => {
    if (!shouldShowError) {
      return;
    }
    errorSubject.next({ error: { msg: errorMessage ?? 'An unknown error occurred.' } });
  }, [errorMessage, errorSubject, shouldShowError]);

  return {
    dataObservable,
    errorObservable,
  };
}
